import React, { useEffect, useState } from 'react';
import { List } from 'immutable';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import PropTypes from 'prop-types';
import { TogglePanel, TogglePanelCollection } from '@els/els-react--toggle-panel';

import BarChartFilter from '@Containers/ResultsFilters/ResultsFiltersComponents/BarChartFilter';
import CheckboxFilter from '@Containers/ResultsFilters/ResultsFiltersComponents/CheckboxFilter';
import StructureFilter from '@Containers/ResultsFilters/ResultsFiltersComponents/StructureFilter';
import FullTextFilter from '@Containers/ResultsFilters/ResultsFiltersComponents/FullTextFilter';

import './ListOfFilters.scss';

const spinnerId = 'ListOfFilters-spinner';
const filtersMap = {
  1: BarChartFilter,
  2: BarChartFilter,
  4: CheckboxFilter,
  5: StructureFilter,
  6: FullTextFilter,
};

function ListOfFilters({ toggleFilter, filters, keyifyParamsForFiltersRequest, paramsForFiltersRequest }) {
  const [expandedPanels, setExpandedPanels] = useState([]);
  const { context } = paramsForFiltersRequest;

  useEffect(() => {
    if (filters) {
      const panels = filters
        .filter((f) => f.get('expanded') === true)
        ?.map((f) => f.get('id'))
        .toArray();
      setExpandedPanels(panels);
      Logger.info('Setting expanded panels:', panels);
    } else {
      setExpandedPanels([]);
    }
  }, [filters]);

  if (!filters) {
    return <RxSpinnerComponent autoStart spinnerId={spinnerId} />;
  }
  const filtersForTogglePanels = filters.filter((f) => [1, 2, 5].includes(f.get('type')));
  const filtersNoTogglePanelsTop = filters.filter((f) => f.get('type') === 6);
  const filtersNoTogglePanelsBottom = filters.filter((f) => f.get('type') === 4);

  if (filters.size !== filtersForTogglePanels.size + filtersNoTogglePanelsBottom.size + filtersNoTogglePanelsTop.size) {
    const filteredOutIds = [
      ...filtersForTogglePanels.map((f) => f.get('id')),
      ...filtersNoTogglePanelsBottom.map((f) => f.get('id')),
      ...filtersNoTogglePanelsTop.map((f) => f.get('id')),
    ];
    const filtersDiff = filters.filter((f) => !filteredOutIds.includes(f.get('id')));
    Logger.warn('Some filters have unsupported types and will not be rendered: ', filtersDiff.toJS());
  }
  // Render non-toggle full-text filters above the toggle panels for prominence, and keep checkbox filters below to preserve the existing filter grouping.
  const renderNonToggleFilter = (filter, extraProps = {}) => {
    const id = filter.get('id');
    const type = filter.get('type');
    const isControlsDisabled = id === 'PROD.TIER';
    const FilterComponent = filtersMap[type];
    return (
      <FilterComponent
        key={id}
        data-e2e={`filter-${id?.replace(/[.\s]/g, '-')}`}
        keyifyParamsForFiltersRequest={keyifyParamsForFiltersRequest}
        context={context}
        filter={filter}
        toggleFilter={toggleFilter}
        disableControls={isControlsDisabled}
        {...extraProps}
      />
    );
  };

  return (
    <div className="list-of-filters">
      {filtersNoTogglePanelsTop.map((filter) => renderNonToggleFilter(filter, { className: 'u-els-margin-top-2x' }))}
      {filtersForTogglePanels && filtersForTogglePanels.size > 0 && (
        <TogglePanelCollection
          data-e2e="list-of-filters-toggle-panels-collection"
          className="list-of-filters-collection"
          headerLevel="3"
          thinKeyline
          noTopKeyline
          initialOpenPanels={expandedPanels}>
          {filtersForTogglePanels
            ?.map((filter) => {
              const id = filter.get('id');
              const type = filter.get('type');
              const FilterComponent = filtersMap[type];
              const isControlsDisabled = id === 'PROD.TIER';

              return (
                <TogglePanel
                  id={id}
                  key={id}
                  data-e2e={`filter-panel-${id?.replace(/[.\s]/g, '-')}`}
                  className="filter-panel"
                  header={filter.get('title') || 'Filter'}
                  onClick={() => {
                    toggleFilter(filter);
                  }}>
                  <FilterComponent
                    data-e2e={`filter-${id?.replace(/[.\s]/g, '-')}`}
                    keyifyParamsForFiltersRequest={keyifyParamsForFiltersRequest}
                    context={context}
                    filter={filter}
                    toggleFilter={toggleFilter}
                    disableControls={isControlsDisabled}
                  />
                </TogglePanel>
              );
            })
            .toArray()}
        </TogglePanelCollection>
      )}
      {filtersNoTogglePanelsBottom.map((filter) => renderNonToggleFilter(filter, { className: 'u-els-margin-top-2x' }))}
    </div>
  );
}

ListOfFilters.propTypes = {
  filters: PropTypes.instanceOf(List),
  toggleFilter: PropTypes.func.isRequired,
  keyifyParamsForFiltersRequest: PropTypes.string.isRequired,
  paramsForFiltersRequest: PropTypes.object.isRequired,
};

export default ListOfFilters;
